<template>
  <div>
    <NavBar sticky data-testid="navbar">
      <template #left>
        <IconButton
          :icon-colors="NebraskaColors.neutralPrimary"
          aria-label="Botão voltar"
          icon-name="ic_arrow_left"
          @click="onBackClick"
        />
      </template>
      <template #center>{{ pageInformations.header }}</template>
    </NavBar>
    <div class="statement__content">
      <template v-if="isLoading">
        <LoaderIndicator data-testid="statement-loader" with-container />
      </template>
      <template v-else-if="hasError">
        <div class="statement__helper-view-container">
          <HelperViewContextual
            v-if="hasErrorIcon"
            class="statement__helper-view"
            :context="errorMessageType"
            :icon="errorMessageIcon"
            :title="errorMessageTitle"
            :description="errorMessageSubTitle"
          />
          <HelperViewStatus
            v-else
            class="statement__helper-view"
            :status="errorMessageType"
            :title="errorMessageTitle"
            :description="errorMessageSubTitle"
            :button-label="errorMessageButton"
            :button-loading="errorMessageButtonLoading"
            :button-disabled="errorMessageButtonDisabled"
            @click="reloadPage"
          />
        </div>
      </template>
      <template v-else-if="hasDownloadError">
        <div
          data-testid="statement-error-modal"
          class="statement__helper-view-container"
        >
          <HelperViewStatus
            class="statement__helper-view"
            :status="errorMessageType"
            :title="errorMessageTitle"
            :description="errorMessageSubTitle"
            :button-label="errorMessageButton"
            :button-loading="errorMessageButtonLoading"
            :button-disabled="errorMessageButtonDisabled"
            @click="handleHelperViewClick"
          />
        </div>
      </template>
      <template v-else>
        <div>
          <Heading :level="3">{{ pageInformations.title }}</Heading>
          <p class="text-p-3">
            {{ pageInformations.subtitle }}
          </p>
          <template v-if="hasYearsAndMonths">
            <div class="statement__container">
              <div
                v-for="statement in statements"
                :key="statement.year"
                class="statement__date-group"
              >
                <Heading
                  :level="4"
                  class="text-p5-semibold"
                  data-testid="statement-year"
                >
                  {{ statement.year }}
                </Heading>
                <Row
                  v-for="month in statement.months"
                  :key="`${statement.year}-${month.value}`"
                  :has-line="false"
                  :has-interaction="true"
                  role="button"
                  tabindex="0"
                  data-testid="statement-month"
                  @click="onClickDownload(statement.year, month.value)"
                  @keydown.native.space.enter="
                    onClickDownload(statement.year, month.value)
                  "
                >
                  <template #top-left>
                    {{ month.name }}
                  </template>
                  <template #top-right>
                    <SpinnerLoader
                      v-if="month.isLoading"
                      class="statement-download__loading"
                      data-testid="statement-month-loader"
                      :size="15"
                      :color="NebraskaColors.elementSecondary"
                    />
                    <BaseIcon
                      v-else
                      icon="ic_download"
                      aria-label="Download"
                      :colors="NebraskaColors.elementSecondary"
                    />
                  </template>
                </Row>
              </div>
            </div>
          </template>
          <template v-else-if="hasYearsAndQuarters">
            <div class="statement__container">
              <div
                v-for="statement in statements"
                :key="statement.year"
                class="statement__date-group"
              >
                <Heading
                  :level="4"
                  class="text-p5-semibold"
                  data-testid="statement-year"
                >
                  {{ statement.year }}
                </Heading>
                <Row
                  v-for="quarter in statement.quarters"
                  :key="`${statement.year}-${quarter.value}`"
                  :has-line="false"
                  :has-interaction="true"
                  role="button"
                  tabindex="0"
                  data-testid="statement-month"
                  @click="onClickQuarterDownload(quarter.value)"
                  @keydown.native.space.enter="
                    onClickQuarterDownload(quarter.value)
                  "
                >
                  <template #top-left>
                    {{ quarter.name }}
                  </template>
                  <template #top-right>
                    <SpinnerLoader
                      v-if="quarter.isLoading"
                      class="statement-download__loading"
                      data-testid="statement-month-loader"
                      :size="15"
                      :color="NebraskaColors.elementSecondary"
                    />
                    <BaseIcon
                      v-else
                      icon="ic_download"
                      aria-label="Download"
                      :colors="NebraskaColors.elementSecondary"
                    />
                  </template>
                </Row>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="statement__date-group__year-group">
              <Row
                v-for="statement in statements"
                :key="statement.year"
                :has-line="false"
                :has-interaction="true"
                role="button"
                tabindex="0"
                data-testid="statement-year"
                @click="onClickDownload(statement.year, 0)"
                @keydown.native.space.enter="onClickDownload(statement.year, 0)"
              >
                <template #top-left>
                  {{ statement.year }}
                </template>
                <template #top-right>
                  <SpinnerLoader
                    v-if="statement.isLoading"
                    class="statement-download__loading"
                    data-testid="statement-month-loader"
                    :size="15"
                    :color="NebraskaColors.elementSecondary"
                  />
                  <BaseIcon
                    v-else
                    icon="ic_download"
                    aria-label="Download"
                    :colors="NebraskaColors.elementSecondary"
                  />
                </template>
              </Row>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  HelperViewContextual,
  HelperViewStatus,
  IconButton,
  BaseIcon,
  BaseText,
  SpinnerLoader,
  Heading,
  NavBar,
  Row,
  Modal,
  ModalButton,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';

import { CustomerModule } from '@/modules/common/store/CustomerModule';
import {
  IStatementFilter,
  Quarter
} from '@/modules/common/services/http/overview';
import { forceFileDownload } from '@/modules/common/helpers/force-download';
import { StatementType } from '@/modules/investments/types/statementType';
import {
  getStatementInformations,
  PageInformations
} from './statement-informations';
import { TranckEvents, getStatementTrackers } from './statement-tracking';

@Component({
  components: {
    HelperViewContextual,
    HelperViewStatus,
    NavBar,
    Heading,
    Row,
    LoaderIndicator,
    BaseText,
    SpinnerLoader,
    BaseIcon,
    IconButton,
    Modal,
    ModalButton
  }
})
export default class StatementDownloadView extends Vue {
  NebraskaColors = NebraskaColors;
  statements: IStatementFilter[] = [];
  isLoading = true;
  hasError = false;
  hasDownloadError = false;
  errorMessage = '';
  hasYearsAndMonths = false;
  hasYearsAndQuarters = false;
  errorMessageTitle = '';
  errorMessageSubTitle = '';
  errorMessageIcon = '';
  errorMessageType = '';
  errorMessageButton = '';
  errorMessageButtonLoading = false;
  errorMessageButtonDisabled = false;
  year = 0;
  month = 0;
  quarter = '';

  created() {
    this.loadData();
  }

  mounted() {
    this.statementTrackers.sendLoadTrack();
  }

  private get statementType() {
    return this.$route.params.statementType as StatementType;
  }

  get pageInformations(): PageInformations {
    return getStatementInformations(this.statementType);
  }

  get statementTrackers(): TranckEvents {
    return getStatementTrackers(this.statementType);
  }

  get hasErrorIcon() {
    return this.errorMessageIcon !== undefined;
  }

  async loadData() {
    try {
      this.hasError = false;
      this.isLoading = true;
      const statements = await this.pageInformations.getFilters(
        this.statementType
      );
      if (statements[0]?.months) {
        this.statements = this.parseYearsAndMonths(statements);
        this.hasYearsAndMonths = true;
      } else if (statements[0]?.quarters) {
        this.statements = statements;
        this.hasYearsAndQuarters = true;
      } else {
        this.statements = statements;
      }
    } catch (error) {
      this.hasError = true;
      this.errorMessage = error?.message || 'Erro ao carregar dados';
    } finally {
      this.isLoading = false;
    }
  }

  async handleHelperViewClick() {
    this.errorMessageButtonLoading = true;
    if (this.errorMessageButton === 'Voltar') {
      this.onBackClick();
    } else {
      this.hasYearsAndQuarters
        ? await this.onClickQuarterDownload(this.quarter)
        : await this.onClickDownload(this.year, this.month);
    }
    this.errorMessageButtonLoading = false;
  }

  reloadPage() {
    return this.$router.go(0);
  }

  private parseYearsAndMonths(
    statements: IStatementFilter[]
  ): IStatementFilter[] {
    return statements.map(statement => ({
      ...statement,
      months: statement.months?.map(month => ({
        ...month,
        isLoading: false
      }))
    }));
  }

  private get customer() {
    return CustomerModule.getCustomer!;
  }

  onBackClick() {
    if (this.hasDownloadError) this.hasDownloadError = false;
    else {
      this.statementTrackers.sendBackTrack();
      this.$router.push({ name: 'home' });
    }
  }

  async onClickDownload(year: number, month: number) {
    try {
      this.toggleItemLoading(year, month);
      const statement = await this.pageInformations.getStatement(
        this.customer._id,
        `${year}-${month.toString().padStart(2, '0')}`,
        month,
        year
      );
      this.hasDownloadError = false;
      this.statementTrackers.sendDownloadTrack(year, month);

      forceFileDownload({
        data: statement,
        type: 'application/pdf',
        fileName: `${this.pageInformations.fileName}-${year}-${month}.pdf`
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      this.toggleItemLoading(year, month);
    }
  }

  async onClickQuarterDownload(quarter: string) {
    try {
      this.quarter = quarter;
      this.toggleQuarterItemLoading(quarter);
      const statement = await this.pageInformations.getStatement(quarter);
      this.hasDownloadError = false;
      this.statementTrackers.sendDownloadTrack(quarter);

      forceFileDownload({
        data: statement,
        type: 'application/pdf',
        fileName: `${this.pageInformations.fileName}-${quarter}.pdf`
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      this.toggleQuarterItemLoading(quarter);
    }
  }

  private async handleError(err: any) {
    const { title, subTitle, icon, type, buttonLabel } =
      await this.pageInformations.getErrorMessage(err);
    this.errorMessageTitle = title;
    this.errorMessageSubTitle = subTitle;
    this.errorMessageIcon = icon;
    this.errorMessageType = type;
    this.errorMessageButton = buttonLabel;

    this.hasDownloadError = true;
  }

  private toggleItemLoading(year: number, month: number) {
    const statementYearIndex = this.statements.findIndex(
      (statement: IStatementFilter) => statement.year === year
    );
    if (this.statements[0]?.months) {
      this.statements[statementYearIndex]?.months?.forEach(
        (statementMonth: any) => {
          if (statementMonth.value === month) {
            statementMonth.isLoading = !statementMonth.isLoading;
          }
        }
      );
    } else {
      this.statements.forEach((statement: IStatementFilter) => {
        if (statement.year === year) {
          (statement as any).isLoading = !(statement as any).isLoading;
        }
      });
    }
  }

  private toggleQuarterItemLoading(quarter: string) {
    const statementYearIndex = this.statements.findIndex(
      (statement: IStatementFilter) => quarter.includes(`${statement.year}`)
    );
    if (statementYearIndex >= 0) {
      this.statements[statementYearIndex]?.quarters?.forEach(
        (statementQuarter: Quarter) => {
          if (statementQuarter.value === quarter) {
            statementQuarter.isLoading = !statementQuarter.isLoading;
          }
        }
      );
    }
  }

  closeErrorModal() {
    this.hasDownloadError = false;
  }
}
</script>

<style lang="less" scoped>
.statement__content {
  max-width: 1200px;
  margin: @size-spacing-x800 auto;
  padding: 0px @size-spacing-x400;
}

.statement__container {
  margin-top: @size-spacing-x600;
}

.statement__date-group {
  &:not(:first-of-type) {
    margin-top: @size-spacing-x800;
  }
  &__year-group {
    margin-top: @spacing-xxm;
    border-top: 1px solid #e3e4eb;
  }
}

.statement__helper-view-container {
  max-width: 1200px;
  height: calc(100vh - 160px);
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

.statement__helper-view {
  width: 320px;
}

.statement-download__loading {
  margin-right: 4px;
}
</style>
